"use client"

import React, {useEffect, useState} from 'react';
import Header2 from "@/app/(index)/header2";
import cx from "clsx";
import {Noto_Sans_TC} from 'next/font/google';
import useGlobalStateStore from "@/app/(index)/use-global-state-store";
import Footer from "@/app/(index)/footer";
import Bread from "@/app/(index)/bread";
import {Button, Divider, Text, Textarea, TextInput, UnstyledButton} from "@mantine/core";
import {useForm, zodResolver} from "@mantine/form";
import {IOpinionDTO} from "@/form/opinion";
import {z} from "zod";
import {opinionSubmit} from "@/service/opinion";
import {IconCaretRight, IconSquareRoundedFilled} from "@tabler/icons-react";
import Link from "next/link";
import {useMediaQuery} from "@mantine/hooks";

const notoSansTC = Noto_Sans_TC({
    weight: ['400', '500', '700'], // 指定粗細
    subsets: ['latin'],     // 指定支援的字符集
    display: 'swap',        // 字型載入行為
});

export default function Page() {
    const { fontSize } = useGlobalStateStore();
    const [tab, setTab] = useState<number>(1);
    const [opinionDone, setOpinionDone] = useState<boolean>(false);
    const loadCurrentFontSize = (s: number, m: number, l: number) => {
        if(fontSize == 0) return `text-[${s}px]`;
        else if(fontSize == 1) return `text-[${m}px]`;
        else if(fontSize == 2) return `text-[${l}px]`;
    }

    useEffect(() => {
        // 確保頁面完全載入後再顯示 alert
        const timer = setTimeout(() => {
            const targetDate = new Date('2025-11-19T18:00:00');
            const currentDate = new Date();

            if (currentDate < targetDate) {
                alert('全文提供服務主機將進行作業環境升級，預計於2025/11/19(三) 10:00~18:00暫停服務，造成您的不便，敬請見諒。');
            }
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    const nonEmptyString = z.string({message: "欄位必填"}).nullable().refine(value => value !== null && value.trim() !== '', {
        message: '欄位必填',
    });

    const schema = z.object({
        name: nonEmptyString,
        mail: z.string().email({message: "EMAIL 格式錯誤"}),
        title: nonEmptyString,
        content: nonEmptyString,
        tel: nonEmptyString,
    });

    const form = useForm<IOpinionDTO>({
        mode: 'controlled',
        validate: zodResolver(schema),
        initialValues: {
            name: '',
            mail: '',
            title: '',
            content: '',
            tel: '',
        }
    });

    const handleFormSubmit = () => {
        opinionSubmit(form.values).then(() => {
            setOpinionDone(true)
        });
    }

    const mediaQueryLessSM = useMediaQuery('(min-width: 480px)');
    return (
        <div className={cx("min-h-screen flex flex-col justify-between", notoSansTC.className)}>
            <div>
                <Header2/>
                {
                    mediaQueryLessSM && (
                        <Bread name="科研資料核心研究團隊 - 全文提供服務"/>
                    )
                }
                <div
                    className={cx("mx-auto mb-[100px]", mediaQueryLessSM ? 'w-[1250px]' : 'w-full mt-[100px] px-[10px]')}>
                    <div
                        className="bg-[url('/images/title_topBG.png')] bg-[length:100%_5px] bg-no-repeat bg-top inline-block pt-[15px] text-[#5b5b5b] text-[35px] font-bold">
                        全文提供服務
                    </div>
                    <div className="mt-[40px]">
                        <div className="flex flex-col gap-8">
                            <div className="flex items-start gap-3">
                                <div className="relative top-[13px]">
                                    <IconSquareRoundedFilled size={10}/>
                                </div>
                                <div>
                                    <Text c="blue" fw={700} size="xl">說明</Text>
                                    <div
                                        className="text-lg">為協助國內各界人士獲取研究參考資料，本中心與國內、外主要資料單位建立資訊流通管道，提供期刊論文、會議論文、研究報告、博碩士論文等全文資料。
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="relative top-[13px]">
                                    <IconSquareRoundedFilled size={10}/>
                                </div>
                                <div>
                                    <div className="flex gap-1 items-center"><Text c="blue" fw={700}
                                                                                   size="xl">帳號申請</Text><Text
                                        c="dark" size={"lg"}>（洽詢電話: 02-2737-7647）</Text></div>
                                    <div className="text-lg">請先完成<Link href="/document-services/account-apply"
                                                                           className="text-blue-700 underline">註冊（Registration）</Link>程序，取得客戶專用帳號及密碼後，建議先登入<Link
                                        target="_blank"
                                        href="https://weblib.stpi.niar.org.tw/8787/olor/owa/ismw.ismwinitactpwd.jsp"
                                        className="text-blue-700 underline">變更密碼</Link> ，再依需要提出<Link
                                        target="_blank"
                                        href="https://weblib.stpi.niar.org.tw/8787/olor/owa/ismwsrvclient.ismwinitclientfc.jsp"
                                        className="text-blue-700 underline">全文申請</Link>。
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="relative top-[13px]">
                                    <IconSquareRoundedFilled size={10}/>
                                </div>
                                <div>
                                    <div className="flex gap-1 items-center"><Text c="blue" fw={700}
                                                                                   size="xl">資料申請</Text><Text
                                        c="dark" size={"lg"}>（洽詢電話: 02-2737-7910）</Text></div>
                                    <div className="text-lg"><Link target="_blank"
                                                                   href="https://weblib.stpi.niar.org.tw/8787/olor/owa/ismwsrvclient.ismwinitclientfc.jsp"
                                                                   className="text-blue-700 underline">全文申請表(Request
                                        Form)</Link> ：請將完整書目資料依序填入此表欄位，如期刊、圖書、會議論文、研究報告、博碩士論文等。
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="relative top-[13px]">
                                    <IconSquareRoundedFilled size={10}/>
                                </div>
                                <div>
                                    <Text c="blue" fw={700} size="xl">查詢資料申請處理情形</Text>
                                    <div className="text-lg">讀者可利用<Link target="_blank"
                                                                             href="https://weblib.stpi.niar.org.tw/8787/olor/owa/ismwrec.ismwinitrec.jsp"
                                                                             className="text-blue-700 underline">線上查詢服務紀錄</Link>，或電話洽詢。
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="relative top-[13px]">
                                    <IconSquareRoundedFilled size={10}/>
                                </div>
                                <div>
                                    <Text c="blue" fw={700} size="xl">收費標準</Text>
                                    <div className="text-lg flex flex-col gap-2">
                                        <div>一、本中心館藏</div>
                                        <ul className="list-inside list-decimal ps-10 leading-8">
                                            <li>一般資料複印紙本：每頁5元，每篇另加服務費40元。</li>
                                            <li>國內博碩士論文（民國83-93年）：微片複印每頁5元，光碟片（限有影像檔者）提供每篇300元，每篇另加服務費40元。</li>
                                            <li>國科會研究報告：紙本複印每頁3元，光碟片（限有影像檔者）提供每篇200元，每篇另加服務費40元。</li>
                                        </ul>
                                        <div>二、委託本中心向國內其他單位申請</div>
                                        <ul className="ps-10">
                                            <li>依提供單位實際收費外，每篇另加費用40元。</li>
                                        </ul>
                                        <div>三、委託本中心向國外申請（資料開始申請則無法取消，收到資料以掛號郵寄）</div>

                                        <ul className="list-inside list-decimal ps-10 leading-8">
                                            <li>期刊論文：每篇450元起，依國外單位收費標準。</li>
                                            <li>UMI博碩士論文：2,700元。</li>
                                            <li>NTIS報告及特殊資料：依國外單位收費標準。</li>
                                            <li>不提供國外借書服務。</li>
                                            <li>國外學位論文不受理部分篇章複印申請。</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="relative top-[13px]">
                                    <IconSquareRoundedFilled size={10}/>
                                </div>
                                <div>
                                    <Text c="blue" fw={700} size="xl">資料提供方式</Text>
                                    <div className="text-lg flex flex-col gap-2">
                                        <div>一、一般為郵遞。</div>
                                        <div>二、傳真方式，資料費外，每頁另加5元。</div>
                                        <div>三、向國外申請取得資料者，以掛號方式寄送讀者。</div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="relative top-[13px]">
                                    <IconSquareRoundedFilled size={10}/>
                                </div>
                                <div>
                                    <Text c="blue" fw={700} size="xl">付款方式</Text>
                                    <div className="text-lg flex flex-col gap-2">
                                        <div>請於收到發票後，登入「線上查詢服務紀錄」點選「線上繳費系統」進行繳費。</div>
                                        <div>目前提供以下付費方式：</div>
                                        <div>一、信用卡付費</div>
                                        <div>二、ATM / WebATM即時繳款。</div>
                                    </div>
                                </div>
                            </div>
                        </div>


                        {/*<ul className="list-inside list-disc">*/}
                        {/*    <li className="text-blue-600 font-bold text-xl">說明</li>*/}
                        {/*    <div className="ps-20 text-[20px]">*/}
                        {/*        請先完成 註冊（Registration）程序，取得客戶專用帳號及密碼後，建議先登入 變更密碼 ，再依需要提出 全文申請 。*/}
                        {/*    </div>*/}
                        {/*    <li className="text-blue-600 font-bold text-xl">*/}
                        {/*        帳號申請 <span className="text-black font-normal">（洽詢電話: 02-2737-7647）</span>*/}
                        {/*    </li>*/}
                        {/*    <li className="text-blue-600 font-bold text-xl">*/}
                        {/*        資料申請 <span className="text-black font-normal">（洽詢電話: 02-2737-7910）</span>*/}
                        {/*    </li>*/}
                        {/*    <li className="text-blue-600 font-bold text-xl">查詢資料申請處理情形</li>*/}
                        {/*    <li className="text-blue-600 font-bold text-xl">收費標準</li>*/}
                        {/*    <li className="text-blue-600 font-bold text-xl">資料提供方式</li>*/}
                        {/*    <li className="text-blue-600 font-bold text-xl">付款方式</li>*/}
                        {/*</ul>*/}


                    </div>
                </div>
            </div>
            <Footer/>
        </div>
    )
        ;
}
